import { Paper, styled, type SvgIconProps } from '@mui/material'
import { CalendarBlankIcon, CaretLeftIcon, CaretRightIcon, type IconProps } from '@phosphor-icons/react'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { deDE, enUS } from '@mui/x-date-pickers/locales'
import { DateValidationError, DateView } from '@mui/x-date-pickers/models'
import { PickersCalendarHeaderProps } from '@mui/x-date-pickers/PickersCalendarHeader'
import { usePickerAdapter, usePickerTranslations } from '@mui/x-date-pickers/hooks'
import { Dayjs } from 'dayjs'
import 'dayjs/locale/de'
import 'dayjs/locale/en'
import { tokens } from 'ComponentLibrary/style-dictionary/output'
import { FocusEventHandler, forwardRef } from 'react'
import { Control, FieldPathByValue, FieldValues, UseControllerProps, useController } from 'react-hook-form'
import { IconButton } from '../../buttons/IconButton/IconButton'
import { isRequiredRule } from '../TextInput/TextInput'
import { Dropdown, type DropdownOption } from '../Dropdown/Dropdown'

export type DatePickerProps = {
    'aria-labelledby'?: string
    autoFocus?: boolean
    className?: string
    defaultValue?: Dayjs | null
    disabled?: boolean
    errorMessage?: string
    format?: string
    label?: string
    locale?: string
    maxDate?: Dayjs
    minDate?: Dayjs
    name?: string
    onAccept?: (value: Dayjs | null) => void
    onBlur?: FocusEventHandler<HTMLInputElement>
    onChange?: (value: Dayjs | null) => void
    onError?: (inputIsInvalid: boolean) => void
    openTo?: DateView
    readOnly?: boolean
    required?: boolean
    value?: Dayjs | null
    views?: readonly DateView[]
}

export const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(function DatePicker(
    {
        'aria-labelledby': ariaLabelledBy,
        autoFocus,
        className,
        defaultValue,
        disabled,
        errorMessage,
        format,
        label,
        locale = 'de-DE',
        maxDate,
        minDate,
        name,
        onAccept,
        onBlur,
        onChange,
        onError,
        openTo = 'day',
        readOnly,
        required,
        value,
        views,
    },
    ref
) {
    const usesEnglishLocale = locale.toLowerCase().startsWith('en')
    const localeText = usesEnglishLocale
        ? enUS.components.MuiLocalizationProvider.defaultProps.localeText
        : deDE.components.MuiLocalizationProvider.defaultProps.localeText

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={usesEnglishLocale ? 'en' : 'de'} localeText={localeText}>
            <DatePickerRoot className={className}>
                <DesktopDatePicker
                    autoFocus={autoFocus}
                    defaultValue={defaultValue}
                    disabled={disabled}
                    enableAccessibleFieldDOMStructure={false}
                    format={format ?? (usesEnglishLocale ? 'MM/DD/YYYY' : 'DD.MM.YYYY')}
                    inputRef={ref}
                    label={label}
                    maxDate={maxDate}
                    minDate={minDate}
                    name={name}
                    onAccept={(newValue) => onAccept?.(newValue)}
                    onChange={(newValue) => onChange?.(newValue)}
                    onError={(reason, newValue) => onError?.(!isValidDate(reason, newValue, required))}
                    openTo={openTo}
                    readOnly={readOnly}
                    reduceAnimations
                    slots={{
                        calendarHeader: DatePickerCalendarHeader,
                        desktopPaper: DatePickerPaper,
                        openPickerIcon: CalendarBlankIcon,
                    }}
                    slotProps={{
                        day: {
                            sx: selectedDayFocusStyles,
                        },
                        openPickerButton: {
                            disableRipple: true,
                        },
                        openPickerIcon: { weight: 'bold' } as SvgIconProps & Pick<IconProps, 'weight'>,
                        popper: {
                            modifiers: [{ name: 'computeStyles', options: { gpuAcceleration: false } }],
                        },
                        textField: {
                            error: errorMessage !== undefined ? true : undefined,
                            helperText: errorMessage,
                            onBlur,
                            required,
                            slotProps: {
                                htmlInput: {
                                    'aria-labelledby': ariaLabelledBy,
                                },
                            },
                        },
                    }}
                    value={value}
                    views={views}
                />
            </DatePickerRoot>
        </LocalizationProvider>
    )
})

function isValidDate(reason: DateValidationError | null, value: Dayjs | null, required = false) {
    return reason === null && (!required || value !== null)
}

type DateFieldPath<TFieldValues extends FieldValues> = FieldPathByValue<TFieldValues, Dayjs | null | undefined>

type FormDatePickerRules<TFieldValues extends FieldValues, TName extends DateFieldPath<TFieldValues>> = NonNullable<
    UseControllerProps<TFieldValues, TName>['rules']
>

export type FormDatePickerProps<
    TFieldValues extends FieldValues,
    TName extends DateFieldPath<TFieldValues> = DateFieldPath<TFieldValues>,
> = Omit<DatePickerProps, 'defaultValue' | 'errorMessage' | 'name' | 'required' | 'value'> & {
    control: Control<TFieldValues>
    defaultValue?: UseControllerProps<TFieldValues, TName>['defaultValue']
    name: TName
    rules?: FormDatePickerRules<TFieldValues, TName>
}

export function FormDatePicker<TFieldValues extends FieldValues, TName extends DateFieldPath<TFieldValues> = DateFieldPath<TFieldValues>>({
    control,
    defaultValue,
    name,
    onBlur,
    onChange,
    rules,
    ...datePickerProps
}: FormDatePickerProps<TFieldValues, TName>) {
    const { field, fieldState } = useController({
        control,
        defaultValue,
        name,
        rules,
    })

    return (
        <DatePicker
            {...datePickerProps}
            errorMessage={fieldState.error ? (fieldState.error.message?.toString() ?? '') : undefined}
            name={field.name}
            onBlur={(event) => {
                field.onBlur()
                onBlur?.(event)
            }}
            onChange={(newValue) => {
                field.onChange(newValue)
                onChange?.(newValue)
            }}
            ref={field.ref}
            required={isRequiredRule(rules?.required)}
            value={field.value ?? null}
        />
    )
}

type CalendarHeaderOption = DropdownOption<number>

const DatePickerCalendarHeader = forwardRef<HTMLDivElement, PickersCalendarHeaderProps>(function DatePickerCalendarHeader(
    { currentMonth, disabled, disableFuture, disablePast, maxDate, minDate, onMonthChange, timezone, view, views },
    ref
) {
    const adapter = usePickerAdapter()
    const translations = usePickerTranslations()

    if (views.length === 1 && views[0] === 'year') {
        return null
    }

    const previousMonth = adapter.addMonths(currentMonth, -1)
    const nextMonth = adapter.addMonths(currentMonth, 1)
    const today = adapter.date(undefined, timezone)

    const isMonthDisabled = (date: Dayjs) =>
        Boolean(disabled) ||
        adapter.isBefore(adapter.endOfMonth(date), adapter.startOfMonth(minDate)) ||
        adapter.isAfter(adapter.startOfMonth(date), adapter.endOfMonth(maxDate)) ||
        Boolean(disablePast && adapter.isBefore(adapter.endOfMonth(date), adapter.startOfMonth(today))) ||
        Boolean(disableFuture && adapter.isAfter(adapter.startOfMonth(date), adapter.endOfMonth(today)))

    const isYearDisabled = (year: number) => {
        const date = adapter.setYear(currentMonth, year)
        return (
            Boolean(disabled) ||
            adapter.isBefore(adapter.endOfYear(date), adapter.startOfYear(minDate)) ||
            adapter.isAfter(adapter.startOfYear(date), adapter.endOfYear(maxDate)) ||
            Boolean(disablePast && adapter.isBefore(adapter.endOfYear(date), adapter.startOfYear(today))) ||
            Boolean(disableFuture && adapter.isAfter(adapter.startOfYear(date), adapter.endOfYear(today)))
        )
    }

    const monthOptions: CalendarHeaderOption[] = Array.from({ length: 12 }, (_, month) => {
        const date = adapter.setMonth(currentMonth, month)
        return {
            label: adapter.format(date, 'monthShort'),
            value: month,
            disabled: isMonthDisabled(date),
        }
    })
    const yearOptions: CalendarHeaderOption[] = adapter.getYearRange([minDate, maxDate]).map((date) => {
        const year = adapter.getYear(date)
        return {
            label: adapter.formatNumber(`${year}`),
            value: year,
            disabled: isYearDisabled(year),
        }
    })

    const changeVisibleMonth = (date: Dayjs) => {
        if (adapter.isBefore(adapter.endOfMonth(date), adapter.startOfMonth(minDate))) {
            onMonthChange(minDate)
            return
        }
        if (adapter.isAfter(adapter.startOfMonth(date), adapter.endOfMonth(maxDate))) {
            onMonthChange(maxDate)
            return
        }
        onMonthChange(date)
    }

    return (
        <DatePickerCalendarHeaderRoot ref={ref}>
            <CalendarHeaderDropdown>
                <Dropdown
                    aria-label={translations.month}
                    disablePortal
                    onValueChange={(month) => {
                        if (month !== undefined) {
                            changeVisibleMonth(adapter.setMonth(currentMonth, month))
                        }
                    }}
                    options={monthOptions}
                    value={adapter.getMonth(currentMonth)}
                />
            </CalendarHeaderDropdown>
            <CalendarHeaderDropdown>
                <Dropdown
                    aria-label={translations.year}
                    disablePortal
                    onValueChange={(year) => {
                        if (year !== undefined) {
                            changeVisibleMonth(adapter.setYear(currentMonth, year))
                        }
                    }}
                    options={yearOptions}
                    value={adapter.getYear(currentMonth)}
                />
            </CalendarHeaderDropdown>
            <CalendarNavigation>
                <IconButton
                    ariaLabel={translations.previousMonth}
                    disabled={isMonthDisabled(previousMonth) || view !== 'day'}
                    icon={<CaretLeftIcon />}
                    onClick={() => onMonthChange(previousMonth)}
                    size="small"
                    variant="tertiary"
                />
                <IconButton
                    ariaLabel={translations.nextMonth}
                    disabled={isMonthDisabled(nextMonth) || view !== 'day'}
                    icon={<CaretRightIcon />}
                    onClick={() => onMonthChange(nextMonth)}
                    size="small"
                    variant="tertiary"
                />
            </CalendarNavigation>
        </DatePickerCalendarHeaderRoot>
    )
})

const DatePickerRoot = styled('div')({
    minHeight: '46px',
    minWidth: 0,
    position: 'relative',
    width: '100%',
    '& .MuiFormControl-root': {
        minHeight: '46px',
        minWidth: 0,
        width: '100%',
    },
    '& .MuiInputLabel-root': {
        ...tokens.TextS,
        color: tokens.ColorTextSubtle,
        maxWidth: 'calc(100% - 48px)',
        transform: `translate(${tokens.SizingSpacing150}, 14px) scale(1)`,
        zIndex: 1,
        '&.Mui-focused': {
            color: tokens.ColorTextSubtle,
        },
        '&.Mui-error': {
            color: tokens.ColorTextDangerSubtle,
        },
        '&.Mui-disabled': {
            color: tokens.ColorTextDisabled,
        },
        '&.MuiInputLabel-shrink': {
            ...tokens.TextXs,
            backgroundColor: tokens.ColorSurfaceDefault,
            maxWidth: 'calc(100% - 24px)',
            padding: `0 ${tokens.SizingSpacing075}`,
            transform: `translate(${tokens.SizingSpacing075}, -7px) scale(1)`,
        },
        '&.MuiInputLabel-shrink.Mui-disabled': {
            backgroundColor: 'transparent',
        },
    },
    '&:has(.MuiOutlinedInput-root:hover, .MuiInputLabel-shrink:hover)': {
        '& .MuiInputLabel-shrink:not(.Mui-focused):not(.Mui-disabled)': {
            backgroundColor: 'transparent',
        },
        '& .MuiOutlinedInput-root:not(.Mui-focused):not(.Mui-disabled)': {
            backgroundColor: tokens.ColorSurfaceHover,
        },
        '& .MuiOutlinedInput-root:not(.Mui-focused):not(.Mui-disabled):not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.ColorBorderDefault,
        },
    },
    '& .MuiOutlinedInput-root': {
        ...tokens.TextS,
        backgroundColor: tokens.ColorSurfaceDefault,
        borderRadius: tokens.SizingCornerS,
        color: tokens.ColorTextBold,
        gap: tokens.SizingSpacing100,
        height: '46px',
        padding: `0 ${tokens.SizingSpacing150}`,
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.ColorBorderDefault,
            borderWidth: tokens.SizingBorderS,
        },
        '&.Mui-focused': {
            backgroundColor: tokens.ColorSurfaceDefault,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.ColorBorderFocused,
            borderWidth: tokens.SizingBorderS,
        },
        '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.ColorBorderDanger,
            borderWidth: tokens.SizingBorderS,
        },
        '&.Mui-disabled': {
            backgroundColor: tokens.ColorSurfaceDisabled,
            color: tokens.ColorTextDisabled,
        },
        '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
            borderColor: `${tokens.ColorBorderDisabled}!important`,
            borderWidth: tokens.SizingBorderS,
        },
    },
    '&:has(.MuiInputLabel-shrink) .MuiOutlinedInput-notchedOutline legend': {
        fontSize: tokens.TextXs.fontSize,
        maxWidth: `calc(100% - ${tokens.SizingSpacing100} - ${tokens.SizingBorderS})`,
    },
    '& .MuiOutlinedInput-input': {
        ...tokens.TextS,
        boxSizing: 'border-box',
        color: tokens.ColorTextBold,
        height: tokens.FontLineheightSm,
        minWidth: 0,
        padding: 0,
        '&.Mui-disabled': {
            color: tokens.ColorTextDisabled,
            WebkitTextFillColor: tokens.ColorTextDisabled,
        },
    },
    '& .MuiInputAdornment-root': {
        color: tokens.ColorIconSubtle,
        height: tokens.SizingIconS,
        marginLeft: 0,
        maxHeight: 'none',
        width: tokens.SizingIconS,
    },
    '& .MuiInputAdornment-root .MuiIconButton-root': {
        color: 'inherit',
        height: tokens.SizingIconS,
        padding: 0,
        width: tokens.SizingIconS,
    },
    '& .MuiInputAdornment-root svg': {
        height: tokens.SizingIconS,
        width: tokens.SizingIconS,
    },
    '& .MuiInputAdornment-root .MuiIconButton-root.Mui-disabled': {
        color: tokens.ColorIconDisabled,
    },
    '& .MuiFormHelperText-root': {
        ...tokens.TextXs,
        boxSizing: 'border-box',
        color: tokens.ColorTextDangerSubtle,
        height: tokens.FontLineheightXs,
        margin: `${tokens.SizingSpacing050} 0 0`,
        overflow: 'hidden',
        padding: `0 ${tokens.SizingSpacing150}`,
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        width: '100%',
    },
})

const DatePickerPaper = styled(Paper)({
    ...tokens.Flyout,
    backgroundColor: tokens.ColorSurfaceDefault,
    borderRadius: tokens.SizingCornerS,
    overflow: 'visible',
    width: '320px',
    '& .MuiPickersLayout-root': {
        minWidth: 0,
        overflow: 'visible',
        width: '320px',
    },
    '& .MuiDateCalendar-root': {
        height: 'auto',
        maxHeight: 'none',
        width: '320px',
    },
    '& .MuiDateCalendar-viewTransitionContainer': {
        width: '100%',
    },
    '& .MuiDayCalendar-root': {
        padding: `0 ${tokens.SizingSpacing150} ${tokens.SizingSpacing150}`,
    },
    '& .MuiDayCalendar-header': {
        columnGap: tokens.SizingSpacing025,
        display: 'grid',
        gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
    },
    '& .MuiDayCalendar-weekDayLabel': {
        ...tokens.TextS,
        color: tokens.ColorTextSubtle,
        height: tokens.SizingSpacing400,
        margin: 0,
        width: 'auto',
    },
    '& .MuiDayCalendar-slideTransition': {
        minHeight: 0,
        overflow: 'hidden',
    },
    '& .MuiDayCalendar-weekContainer': {
        columnGap: tokens.SizingSpacing025,
        display: 'grid',
        gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
        margin: `${tokens.SizingSpacing025} 0 0`,
    },
    '& .MuiPickersDay-root': {
        ...tokens.TextS,
        backgroundColor: tokens.ColorSurfaceInverseDefault,
        border: `${tokens.SizingBorderS} solid transparent`,
        borderRadius: tokens.SizingCornerS,
        boxSizing: 'border-box',
        color: tokens.ColorTextBold,
        height: tokens.SizingSpacing400,
        margin: 0,
        width: '100%',
        '&:hover, &.Mui-focusVisible': {
            backgroundColor: tokens.ColorSurfaceInverseHover,
        },
        '&:active': {
            backgroundColor: tokens.ColorSurfaceInversePressed,
        },
        '&.MuiPickersDay-today:not(.Mui-selected)': {
            backgroundColor: tokens.ColorSurfaceInformativeInverseDefault,
            borderColor: 'transparent',
            color: tokens.ColorTextInformativeDefault,
        },
        '&.MuiPickersDay-today:not(.Mui-selected):hover, &.MuiPickersDay-today:not(.Mui-selected).Mui-focusVisible': {
            backgroundColor: tokens.ColorSurfaceInformativeInverseHover,
        },
        '&.MuiPickersDay-today:not(.Mui-selected):active': {
            backgroundColor: tokens.ColorSurfaceInformativeInversePressed,
        },
        '&.Mui-selected': {
            backgroundColor: tokens.ColorSurfaceSelectedDefault,
            borderColor: tokens.ColorBorderSelectedDefault,
            color: tokens.ColorTextBold,
        },
        '&.Mui-selected:hover, &.Mui-selected.Mui-focusVisible': {
            backgroundColor: tokens.ColorSurfaceSelectedHover,
        },
        '&.Mui-selected:active': {
            backgroundColor: tokens.ColorSurfaceSelectedPressed,
        },
        '&.Mui-disabled:not(.Mui-selected)': {
            backgroundColor: tokens.ColorSurfaceDisabled,
            color: tokens.ColorTextDisabled,
        },
        '&.Mui-selected.Mui-disabled': {
            backgroundColor: tokens.ColorSurfaceSelectedDisabled,
            borderColor: tokens.ColorBorderSelectedDisabled,
            color: tokens.ColorTextDisabled,
            opacity: 1,
        },
    },
})

const selectedDayFocusStyles = {
    '&.Mui-selected:focus': {
        backgroundColor: tokens.ColorSurfaceSelectedDefault,
        borderColor: tokens.ColorBorderSelectedDefault,
        color: tokens.ColorTextBold,
    },
}

const DatePickerCalendarHeaderRoot = styled('div')({
    alignItems: 'center',
    columnGap: tokens.SizingSpacing100,
    display: 'flex',
    height: '46px',
    margin: `${tokens.SizingSpacing150} 0 ${tokens.SizingSpacing100}`,
    padding: `0 ${tokens.SizingSpacing150}`,
    width: '100%',
})

const CalendarHeaderDropdown = styled('div')({
    flex: '1 1 0',
    minWidth: 0,
    '&& .MuiOutlinedInput-root': {
        backgroundColor: tokens.ColorSurfaceInverseDefault,
    },
    '&& .MuiOutlinedInput-notchedOutline': {
        border: 0,
    },
    '&&:has(.MuiOutlinedInput-root:hover) .MuiOutlinedInput-root:not(.Mui-focused):not(.Mui-disabled)': {
        backgroundColor: tokens.ColorSurfaceInverseHover,
    },
    '&& .MuiOutlinedInput-root.Mui-focused': {
        backgroundColor: tokens.ColorSurfaceInverseDefault,
    },
})

const CalendarNavigation = styled('div')({
    alignItems: 'center',
    display: 'flex',
    flex: '0 0 auto',
    gap: tokens.SizingSpacing025,
})
